import { Checkbox, Input, message, Modal, Spin } from 'antd';
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import accountApis from '../../../services/accountApis';
import { AxiosResponse } from 'axios';
import useGetEmployees from '../../employees/services/hooks/useGetEmployees';
import { RoleStatus } from '../../employees/components/AddOrUpdateEmployeeModal';
import { formatPhone } from '../../../utils/formatPhone';

interface Props {}
export interface AssignRoleModalRef {
  open: (accountIds: string[], success?: () => void) => void;
  close: () => void;
}

const AssignRoleModal = forwardRef<AssignRoleModalRef, Props>(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const refSuccess = useRef<() => void>();
  const [searchValue, setSearchValue] = useState('');
  const { data: employees, loading: loadingEmployees, getEmployees } = useGetEmployees(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');

  const onOpen = (accountIds: string[], success?: () => void) => {
    getEmployees({ page: 1, size: 100 });
    setOpen(true);
    setAccountIds(accountIds);
    refSuccess.current = success;
  };

  const onClose = () => {
    setSelectedEmployeeId('');
    setSearchValue('');
    refSuccess.current = undefined;
    setOpen(false);
    setAccountIds([]);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const onDone = async () => {
    setLoading(true);
    try {
      const response: AxiosResponse<{ data: any }> = await accountApis.assignRole({
        employeeId: selectedEmployeeId,
        accountIds,
      });
      if (response.data.data) {
        message.success('Assign role successfully');
        refSuccess.current?.();
        onClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const renderList = useMemo(() => {
    // match search value in name or phone, filter RoleStatus = MANAGER or SALE
    const isAcceptRole = [RoleStatus.MANAGER, RoleStatus.SALE];
    const list = employees?.content.filter(
      (item) =>
        (item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.phone.includes(searchValue)) &&
        isAcceptRole.includes(item.roleName as RoleStatus)
    );
    return list;
  }, [employees?.content, searchValue]);

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      width={1048}
      centered
      destroyOnClose={false}
      closable={false}
    >
      <ContainerStyled>
        <TitleStyled>Assign Role</TitleStyled>

        <ListContainerStyled>
          <Input
            placeholder='Search Manager/Sale name or phone'
            style={{ width: '100%', height: '48px', borderRadius: '4px', fontSize: 18 }}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <EmployeeContainerStyled>
            {renderList?.length && renderList?.length > 0 ? (
              renderList?.map((item) => (
                <EmployeeItemStyled
                  key={item.id}
                  className='item-employee'
                  onClick={() => setSelectedEmployeeId(item.id)}
                >
                  <div className='left'>
                    <Checkbox checked={selectedEmployeeId === item.id} />
                    <div
                      className={`role ${
                        item.roleName === RoleStatus.MANAGER ? 'manager' : 'sale'
                      }`}
                    >
                      {item.roleName}
                    </div>
                    <div className='name'>{item.name}</div>
                  </div>
                  <div className='phone'>{formatPhone(item.phone)}</div>
                </EmployeeItemStyled>
              ))
            ) : (
              <Spin spinning={loadingEmployees}>
                {loadingEmployees ? <div>Loading...</div> : <div>No data</div>}
              </Spin>
            )}
          </EmployeeContainerStyled>
        </ListContainerStyled>
        <ButtonsStyled>
          <button type='button' className='btn-cancel' onClick={onClose}>
            Close
          </button>
          <button type='button' className='btn-submit' onClick={onDone} disabled={loading}>
            Assign
          </button>
        </ButtonsStyled>
      </ContainerStyled>
    </Modal>
  );
});
AssignRoleModal.displayName = 'AssignRoleModal';

export default AssignRoleModal;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .ant-form-item {
    margin-bottom: 8px;
  }
`;

const TitleStyled = styled.div`
  align-self: stretch;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ButtonsStyled = styled.div`
  display: flex;
  gap: 16px;

  button {
    cursor: pointer;
    display: flex;
    height: 48px;
    padding: 16px 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;

    text-align: center;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    &.btn-cancel {
      background: #e3e9ed;
      color: #566a7f;
    }
    &.btn-submit {
      color: #fff;
      background: #ff8890;
    }
    &:hover {
      opacity: 0.8;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const ListContainerStyled = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  background: #f6f7fc;
`;

const EmployeeContainerStyled = styled.div`
  min-height: 500px;
  max-height: 600px;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  background: #fff;
  .item-employee:not(:last-child) {
    border-bottom: 1px solid #dadada;
  }
`;

const EmployeeItemStyled = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  padding: 8px;
  &:hover {
    background: #f6f7fc;
  }
  .phone {
    color: #1d2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .left {
    display: flex;
    align-items: center;
    gap: 8px;
    .name {
      color: #1d2129;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
    .role {
      display: flex;
      padding: 0 4px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      color: #fff;
      font-family: Poppins;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      border-radius: 4px;
      &.manager {
        background: #d2464f;
      }
      &.sale {
        background: #2d6772;
      }
    }
    .ant-checkbox-inner {
      width: 31px !important;
      height: 31px !important;
      border-radius: 2px !important;
      &::after {
        inset-inline-start: 30% !important;
        height: 15px !important;
        width: 10px !important;
        top: 45% !important;
      }
    }
  }
`;
